import { nanoid } from 'nanoid';
import { db } from './db';

/**
 * Local image store. Pasted/dropped images are kept as blobs in IndexedDB and
 * referenced from markdown with the `mbimg://<id>` scheme. A resolver swaps
 * those refs for object URLs in the live preview and for data URLs in exports.
 * Images are device-local: they are not part of a shared snapshot (plain text).
 */
export const MBIMG_SCHEME = 'mbimg://';

/** True for the custom local-image URI scheme. */
export function isMbimg(url: string | null | undefined): boolean {
  return !!url && url.startsWith(MBIMG_SCHEME);
}

/** Extract the image id from an `mbimg://<id>` ref. */
export function mbimgId(url: string): string {
  return url.slice(MBIMG_SCHEME.length);
}

/** Store an image blob, returning its `mbimg://<id>` ref for markdown. */
export async function putImage(blob: Blob): Promise<string> {
  const id = nanoid();
  await db.images.add({ id, blob, type: blob.type || 'image/png', createdAt: Date.now() });
  return `${MBIMG_SCHEME}${id}`;
}

export async function getImage(id: string): Promise<Blob | undefined> {
  return (await db.images.get(id))?.blob;
}

/** Read an image as a data URL (used by exports so files are self-contained). */
export async function imageDataUrl(id: string): Promise<string | null> {
  const blob = await getImage(id);
  if (!blob) return null;
  return await new Promise((resolve) => {
    const r = new FileReader();
    r.onload = () => resolve(typeof r.result === 'string' ? r.result : null);
    r.onerror = () => resolve(null);
    r.readAsDataURL(blob);
  });
}

/**
 * Resolve every `mbimg://` image inside a rendered container to a live object
 * URL. Returns a revoke function to free the URLs when the container is torn
 * down or re-rendered.
 */
export async function resolveImagesInDom(root: HTMLElement): Promise<() => void> {
  const urls: string[] = [];
  const imgs = root.querySelectorAll<HTMLImageElement>('img');
  await Promise.all(
    [...imgs].map(async (img) => {
      const raw = img.getAttribute('src');
      if (!isMbimg(raw)) return;
      const blob = await getImage(mbimgId(raw!));
      if (!blob) {
        img.setAttribute('alt', (img.getAttribute('alt') || 'image') + ' (missing)');
        return;
      }
      const url = URL.createObjectURL(blob);
      urls.push(url);
      img.src = url;
    }),
  );
  return () => urls.forEach((u) => URL.revokeObjectURL(u));
}

/** Replace `mbimg://` refs in an HTML string with inline data URLs (exports). */
export async function inlineImagesInHtml(html: string): Promise<string> {
  const refs = [...html.matchAll(/mbimg:\/\/([A-Za-z0-9_-]+)/g)].map((m) => m[1]!);
  if (refs.length === 0) return html;
  const unique = [...new Set(refs)];
  const map = new Map<string, string>();
  await Promise.all(
    unique.map(async (id) => {
      const data = await imageDataUrl(id);
      if (data) map.set(id, data);
    }),
  );
  return html.replace(/mbimg:\/\/([A-Za-z0-9_-]+)/g, (whole, id: string) => map.get(id) ?? whole);
}

/**
 * Delete images that are no longer referenced by any document. Cheap GC to run
 * occasionally (e.g. after emptying trash) so removed images don't linger.
 */
export async function collectGarbageImages(allContent: string[]): Promise<number> {
  const used = new Set<string>();
  for (const c of allContent) {
    for (const m of c.matchAll(/mbimg:\/\/([A-Za-z0-9_-]+)/g)) used.add(m[1]!);
  }
  const all = await db.images.toArray();
  const orphans = all.filter((i) => !used.has(i.id)).map((i) => i.id);
  if (orphans.length) await db.images.bulkDelete(orphans);
  return orphans.length;
}
